import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { useStore } from '../context/StoreContext';
import { 
  Plus, Loader2, Trash2, Search, Users, Package, 
  ShoppingCart, CheckCircle, Clock, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CustomerRequests() {
  const { storeName } = useStore();
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({ product_name: '', notes: '', store: 'orewa' });
  const [editRequest, setEditRequest] = useState(null);

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer-requests`, { headers: getAuthHeader() });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleCreate = async () => {
    if (!form.product_name.trim()) {
      toast.error('Product name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/customer-requests`, form, { headers: getAuthHeader() });
      toast.success('Request added');
      setShowCreateDialog(false);
      setForm({ product_name: '', notes: '', store: 'orewa' });
      fetchRequests();
    } catch (error) {
      toast.error('Failed to add request');
    } finally {
      setSaving(false);
    }
  };

  const handleIncrement = async (id) => {
    try {
      await axios.put(`${API_URL}/customer-requests/${id}/increment`, {}, { headers: getAuthHeader() });
      fetchRequests();
      toast.success('+1 request added');
    } catch (error) {
      toast.error('Failed to increment');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/customer-requests/${id}`, { status: newStatus }, { headers: getAuthHeader() });
      fetchRequests();
      toast.success(`Marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try {
      await axios.delete(`${API_URL}/customer-requests/${id}`, { headers: getAuthHeader() });
      fetchRequests();
      toast.success('Request deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEditSave = async () => {
    if (!editRequest) return;
    try {
      await axios.put(`${API_URL}/customer-requests/${editRequest.id}`, {
        notes: editRequest.notes
      }, { headers: getAuthHeader() });
      toast.success('Updated');
      setEditRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const filteredRequests = requests.filter(r => {
    const matchSearch = r.product_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusConfig = {
    new: { color: 'bg-red-100 text-red-700', label: 'New', icon: Package },
    ordered: { color: 'bg-amber-100 text-amber-700', label: 'Ordered', icon: ShoppingCart },
    oos: { color: 'bg-red-200 text-red-800', label: 'OOS', icon: Package },
    stocked: { color: 'bg-green-100 text-green-700', label: 'Stocked', icon: CheckCircle },
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Customer Requests</h1>
          <p className="text-sm text-slate-500 mt-1">Track products customers are asking for</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-request-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Request
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Search products..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="request-search"
          />
        </div>
        <div className="flex gap-1">
          {['all', 'new', 'ordered', 'oos', 'stocked'].map(status => (
            <Button
              key={status}
              variant={filterStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(status)}
              className={filterStatus === status ? 'bg-slate-900' : ''}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              {status !== 'all' && (
                <span className="ml-1 text-xs">({requests.filter(r => r.status === status).length})</span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{requests.filter(r => r.status === 'new').length}</p>
              <p className="text-xs text-slate-500">New Requests</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{requests.filter(r => r.status === 'ordered').length}</p>
              <p className="text-xs text-slate-500">On Order</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{requests.filter(r => r.status === 'stocked').length}</p>
              <p className="text-xs text-slate-500">Stocked</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Request List */}
      <div className="space-y-3">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No requests found</p>
          </div>
        ) : (
          filteredRequests.map(req => {
            const sc = statusConfig[req.status] || statusConfig.new;
            const StatusIcon = sc.icon;
            return (
              <Card key={req.id} className="hover:shadow-md transition-shadow" data-testid={`request-${req.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-slate-900 text-lg">{req.product_name}</h3>
                          <Badge className={`${sc.color} text-xs`}>
                            <StatusIcon className="w-3 h-3 mr-1" /> {sc.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" /> 
                            <strong className="text-slate-700">{req.request_count}</strong> {req.request_count === 1 ? 'person' : 'people'} asking
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {formatDate(req.created_at)}
                          </span>
                          {req.last_requested !== req.created_at && (
                            <span className="text-xs text-slate-400">Last: {formatDate(req.last_requested)}</span>
                          )}
                          <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{req.store}</span>
                        </div>
                        {req.notes && <p className="text-sm text-slate-500 mt-1">{req.notes}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {/* +1 Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleIncrement(req.id)}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        title="Another customer asked for this"
                        data-testid={`increment-${req.id}`}
                      >
                        <TrendingUp className="w-3.5 h-3.5 mr-1" /> +1
                      </Button>
                      {/* Status buttons */}
                      {req.status === 'new' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(req.id, 'ordered')}
                          className="border-amber-300 text-amber-600 hover:bg-amber-50"
                          data-testid={`mark-ordered-${req.id}`}
                        >
                          <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Ordered
                        </Button>
                      )}
                      {req.status === 'ordered' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(req.id, 'stocked')}
                          className="border-green-300 text-green-600 hover:bg-green-50"
                          data-testid={`mark-stocked-${req.id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Stocked
                        </Button>
                      )}
                      {req.status === 'ordered' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(req.id, 'oos')}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`mark-oos-${req.id}`}
                        >
                          <Package className="w-3.5 h-3.5 mr-1" /> OOS
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditRequest({...req})}
                        className="text-slate-500 hover:text-slate-700"
                        data-testid={`edit-${req.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(req.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-${req.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Package className="w-5 h-5 text-slate-600" /> New Customer Request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Product Name *</label>
              <Input 
                value={form.product_name} 
                onChange={(e) => setForm({...form, product_name: e.target.value})} 
                placeholder="What product is the customer asking for?"
                autoFocus
                data-testid="request-product-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Store</label>
              <select
                value={form.store}
                onChange={(e) => setForm({...form, store: e.target.value})}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              >
                <option value="orewa">{storeName('orewa')}</option>
                <option value="eastridge">{storeName('eastridge')}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Notes</label>
              <Input 
                value={form.notes} 
                onChange={(e) => setForm({...form, notes: e.target.value})} 
                placeholder="Brand preference, size, etc."
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-request">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editRequest} onOpenChange={(open) => !open && setEditRequest(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Request</DialogTitle>
          </DialogHeader>
          {editRequest && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Product</label>
                <p className="text-lg font-semibold text-slate-900">{editRequest.product_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notes (e.g., out of stock, not available, discontinued)</label>
                <textarea
                  value={editRequest.notes || ''}
                  onChange={(e) => setEditRequest({...editRequest, notes: e.target.value})}
                  placeholder="e.g., Discontinued by supplier, OOS until March, Customer prefers Solgar brand..."
                  rows={3}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditRequest(null)}>Cancel</Button>
            <Button onClick={handleEditSave} className="bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
